import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { Observable, Subject, Subscription, interval, takeUntil } from 'rxjs';
import { PhoneNumberType } from '../../models/phone-number-type.enum';
import { PhoneNumberConnectivityResultDto } from '../../models/phone-number-connectivity-result.dto';
import { TestStep } from '../../models/test-step.model';
import { TestStepStatus } from '../../models/test-step-status.model';
import { DisplayDateTimePipe } from 'src/core/pipes/display-date-time.pipe';

export enum PanelStatus {
  Running = 'running',
  Success = 'success',
  Failed = 'failed',
}

@Component({
  selector: 'app-phone-number-test-dialog',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, DialogModule, DisplayDateTimePipe],
  templateUrl: './phone-number-test-dialog.component.html',
  styleUrl: './phone-number-test-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneNumberTestDialogComponent implements OnChanges, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly localization = inject(LocalizationService);

  readonly TestStepStatus = TestStepStatus;
  readonly PanelStatus = PanelStatus;

  @Input() visible = false;
  @Input() phoneNumber = '';
  @Input() kind: PhoneNumberType = PhoneNumberType.Inbound;
  @Input() test$: Observable<PhoneNumberConnectivityResultDto> | null = null;
  @Input() runToken = 0;

  @Output() visibleChange = new EventEmitter<boolean>();

  startTime: Date | null = null;
  elapsedSeconds = 0;
  steps: TestStep[] = [];
  panelStatus: PanelStatus = PanelStatus.Running;
  isRunning = false;

  private readonly destroy$ = new Subject<void>();
  private timerSub?: Subscription;
  private testSub?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['runToken'] && this.runToken > 0 && this.test$) {
      this.startTest();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formattedElapsed(): string {
    const m = Math.floor(this.elapsedSeconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (this.elapsedSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  get panelTitle(): string {
    if (this.isRunning) {
      return this.localization.instant('Settings::PhoneNumbers:TestInProgress');
    }
    if (this.panelStatus === PanelStatus.Success) {
      return this.localization.instant('Settings::PhoneNumbers:TestSuccessful');
    }
    return this.localization.instant('Settings::PhoneNumbers:TestFailed');
  }

  onHide(): void {
    this.cleanup();
    this.visibleChange.emit(false);
  }

  close(): void {
    this.visible = false;
    this.onHide();
  }

  private startTest(): void {
    if (!this.test$) {
      return;
    }
    this.cleanup();

    this.isRunning = true;
    this.elapsedSeconds = 0;
    this.panelStatus = PanelStatus.Running;
    this.startTime = new Date();
    this.steps = this.buildSteps();
    this.cdr.markForCheck();

    this.timerSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.elapsedSeconds++;
        this.cdr.markForCheck();
      });

    this.testSub = this.test$.pipe(takeUntil(this.destroy$)).subscribe({
      next: result => this.handleResult(result),
      error: err => this.handleError(err),
    });
  }

  private buildSteps(): TestStep[] {
    const steps: TestStep[] = [
      {
        label: 'Settings::PhoneNumbers:FreeSwitchServiceReachable',
        status: TestStepStatus.InProgress,
      },
    ];
    if (this.kind === PhoneNumberType.Inbound) {
      steps.push({
        label: 'Settings::PhoneNumbers:IvrServiceReachable',
        status: TestStepStatus.Pending,
      });
    }
    return steps;
  }

  private handleResult(result: PhoneNumberConnectivityResultDto): void {
    if (result.freeSwitchReachable) {
      this.setStep(0, TestStepStatus.Successful);
    } else {
      this.setStep(
        0,
        TestStepStatus.Failed,
        result.freeSwitchError ||
          this.localization.instant('Settings::PhoneNumbers:FreeSwitchServiceNotReachable'),
      );
    }
    this.cdr.markForCheck();

    if (this.kind !== PhoneNumberType.Inbound) {
      this.finalize();
      return;
    }

    this.setStep(1, TestStepStatus.InProgress);
    this.cdr.markForCheck();

    const ivr = result.ivrResult ?? null;
    const ivrSuccess = !!ivr && ivr.isReachable === true;
    if (ivrSuccess) {
      this.setStep(1, TestStepStatus.Successful);
    } else {
      this.setStep(
        1,
        TestStepStatus.Failed,
        this.localization.instant('Settings::PhoneNumbers:IvrServiceNotReachable'),
      );
    }
    this.finalize();
  }

  private handleError(err: unknown): void {
    this.setStep(0, TestStepStatus.Failed, this.extractErrorMessage(err));
    if (this.kind === PhoneNumberType.Inbound) {
      this.setStep(
        1,
        TestStepStatus.Failed,
        this.localization.instant('Settings::PhoneNumbers:IvrServiceCouldNotBeTested'),
      );
    }
    this.finalize();
  }

  private setStep(index: number, status: TestStepStatus, errorMessage?: string): void {
    if (!this.steps[index]) {
      return;
    }
    this.steps[index] = { ...this.steps[index], status, errorMessage };
  }

  private extractErrorMessage(err: unknown): string {
    const fallback = this.localization.instant('Settings::PhoneNumbers:ConnectionFailed');
    if (!err || typeof err !== 'object') {
      return fallback;
    }
    const anyErr = err as { message?: string; error?: { message?: string } };
    return anyErr?.error?.message || anyErr?.message || fallback;
  }

  private finalize(): void {
    this.timerSub?.unsubscribe();
    this.isRunning = false;
    const allSuccess = this.steps.every(s => s.status === TestStepStatus.Successful);
    this.panelStatus = allSuccess ? PanelStatus.Success : PanelStatus.Failed;
    this.cdr.markForCheck();
  }

  private cleanup(): void {
    this.timerSub?.unsubscribe();
    this.testSub?.unsubscribe();
    this.timerSub = undefined;
    this.testSub = undefined;
  }
}
